/**
 * Sentry configuration for React frontend
 */

import * as Sentry from "@sentry/react";

// Get Sentry DSN from environment variables
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT =
  import.meta.env.VITE_SENTRY_ENVIRONMENT ||
  import.meta.env.MODE ||
  "development";
const SENTRY_SAMPLE_RATE = parseFloat(
  import.meta.env.VITE_SENTRY_SAMPLE_RATE || "1.0",
);
const SENTRY_TRACES_SAMPLE_RATE = parseFloat(
  import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "1.0",
);

export const initSentry = (): void => {
  console.log("🔍 Sentry Configuration Debug:");
  console.log("Sentry DSN value:", SENTRY_DSN);
  console.log("Environment:", SENTRY_ENVIRONMENT);
  console.log("Sample Rate:", SENTRY_SAMPLE_RATE);
  console.log("Traces Sample Rate:", SENTRY_TRACES_SAMPLE_RATE);
  console.log("All env vars:", {
    VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
    VITE_SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    MODE: import.meta.env.MODE,
    DEV: import.meta.env.DEV,
    PROD: import.meta.env.PROD,
  });

  if (!SENTRY_DSN) {
    console.warn(
      "❌ SENTRY_DSN not configured, skipping Sentry initialization",
    );
    console.warn("Please set VITE_SENTRY_DSN environment variable");
    return;
  }

  // In development, skip Sentry initialization to avoid network errors
  if (SENTRY_ENVIRONMENT === "development") {
    console.log(
      "🔧 Development mode: Skipping Sentry initialization to avoid network errors",
    );
    console.log("💡 Sentry will be available in production mode");
    console.log("✅ Development mode: Error monitoring disabled");
    return;
  }

  console.log("✅ SENTRY_DSN found, initializing Sentry...");

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    sampleRate: SENTRY_SAMPLE_RATE,
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
    debug: false, // Disable debug in production
    // Release tracking
    release: `maat-ai-frontend@${SENTRY_ENVIRONMENT}`,
    // Filter events before sending
    beforeSend(event, hint) {
      console.log("📤 Sentry beforeSend called:", event);

      // Filter out common production errors
      if (event.exception) {
        const error = hint.originalException;
        if (error instanceof Error) {
          // Filter out ResizeObserver errors
          if (error.message.includes("ResizeObserver loop limit exceeded")) {
            console.log("🚫 Filtering out ResizeObserver error");
            return null;
          }
        }
      }
      console.log("✅ Sending event to Sentry:", event.event_id);
      return event;
    },
    // Set initial scope
    initialScope: {
      tags: {
        service: "maat-ai-frontend",
        version: "0.1.0",
      },
      contexts: {
        app: {
          name: "Maat AI Frontend",
          version: "0.1.0",
          environment: SENTRY_ENVIRONMENT,
        },
      },
    },
  });

  console.log("✅ Sentry initialized successfully!");
  console.log("🔗 Check your Sentry dashboard for incoming events");

  // Set user context if available
  const user = localStorage.getItem("user");
  if (user) {
    try {
      const userData = JSON.parse(user);
      Sentry.setUser({
        id: userData.id,
        email: userData.email,
        username: userData.username,
      });
    } catch (error) {
      console.warn("Failed to parse user data for Sentry:", error);
    }
  }
};

// Utility functions for manual error reporting
export const captureException = (
  error: Error,
  context?: Record<string, unknown>,
): string => {
  return Sentry.captureException(error, {
    contexts: context ? { custom: context } : undefined,
  });
};

export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, unknown>,
): string => {
  return Sentry.captureMessage(message, {
    level,
    contexts: context ? { custom: context } : undefined,
  });
};

export const addBreadcrumb = (
  message: string,
  category: string = "custom",
  level: Sentry.SeverityLevel = "info",
  data?: Record<string, unknown>,
): void => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
};

export const setUserContext = (user: {
  id: string;
  email?: string;
  username?: string;
}): void => {
  Sentry.setUser(user);
};

export const clearUserContext = (): void => {
  Sentry.setUser(null);
};

// Error boundary fallback component
const ErrorFallback = ({
  resetError,
}: {
  error: Error;
  resetError: () => void;
}) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
      <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
        <svg
          className="w-6 h-6 text-red-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      </div>
      <div className="mt-4 text-center">
        <h3 className="text-lg font-medium text-gray-900">
          Something went wrong
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          We're sorry, but something unexpected happened. Please try refreshing
          the page.
        </p>
        <div className="mt-4">
          <button
            onClick={resetError}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  </div>
);

// Error boundary component
export const SentryErrorBoundary = Sentry.withErrorBoundary(ErrorFallback, {
  beforeCapture: (scope, _error, errorInfo) => {
    scope.setTag("errorBoundary", true);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    scope.setContext("errorInfo", errorInfo as any);
  },
});

// Performance monitoring wrapper
export const withProfiler = Sentry.withProfiler;
